import Vue from 'vue';
import { getDataRows, generateCSV } from './KeyPhrases.utils';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import SegmentationChart from 'components/charts/SegmentationChart/SegmentationChart.vue';
import Tooltip from 'components/DataWidgets/KeyPhrases/Tooltip.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-keyphrases.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import { debounce, isEmpty, isEqual } from 'lodash';
import { fetch_keyphrases_data } from 'src/store/modules/data/api';
import { makeBarChartSlide } from '../DataWidgetUtils';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
export default Vue.extend({
    components: {
        WidgetMessagePanel,
        DownloadExportButton,
        SegmentationChart,
        WidgetFrame,
        WidgetMenu,
        Tooltip,
    },
    props: {
        devMode: { type: Boolean, required: false, default: false },
        data: { type: Object, required: false, default: null },
        queries: { type: Array, required: false, default: () => [] },
        /** This group is either a query name, or a concept name, or `overall__`.
         * At the time of writing, it isn't currently used directly, but it is
         * required to allow drilldown->drilldown route changes to correctly
         * update this widget. For drilldown->drilldown, only route query params
         * are changed, and there is no other conduit beside `group` to observe
         * that reactive change. Here this is managed through a watch on `group`.*/
        group: { type: String, required: false, default: '' },
        exportName: { type: String, required: false, default: '' },
        zoomToRoute: { type: Object, required: false, default: null },
        isZoomed: { type: Boolean, required: false, default: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        showDrilldown: { type: Boolean, required: false, default: false },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        /** The title of the current view so that we can use it in the
         * interaction menu. Should be something like "store & prices" */
        viewTitle: { type: String, required: false, default: '' },
        groupbyNotSupported: { type: Boolean, default: false },
    },
    data() {
        return {
            icon,
            errorIcon,
            hasErrored: false,
            width: 0,
            height: 0,
            tooltipStats: null,
            headings: [],
            sortSelection: 'npmi',
            sortAsc: false,
            customDateRange: [undefined, undefined],
            displaySelection: 'Correlation',
            minConceptFreq: 0,
        };
    },
    computed: {
        /** This is only to change the type to the correct type to make
         * TypeScript happy. It would be great if we could just say
         * `$el as HTMLElement` up there in the template, but that seems
         * to not be allowed in Vue 2.*/
        bindElement() {
            return this.$el;
        },
        isLoading() {
            return this.data === null && !this.hasValidData;
        },
        conceptList() {
            if (!isEmpty(this.data))
                return this.data['payload'];
            else
                return [];
        },
        limit() {
            if (this.isZoomed) {
                return 100;
            }
            else {
                return 10;
            }
        },
        minValue() {
            if (this.displaySelection === 'Frequency') {
                return 0;
            }
            else {
                if (isEmpty(this.conceptList)) {
                    return 0;
                }
                // Dynamically change the lower bound of the npmi
                // axis based on whether there are negative values
                // or not. There is an API parameter to the key phrases
                // endpoint called `positive_only` that controls whether
                // negative values are returned or not. The dynamic axis
                // bound below will work regardless of whether the
                // `positive_only` parameter is set to true or false.
                let minNPMI = Math.min(...this.conceptList.map((item) => item.npmi));
                if (minNPMI < 0) {
                    return -1;
                }
                else {
                    return 0;
                }
            }
        },
        hasValidData() {
            return this.dataRows.length > 0;
        },
        dataRows() {
            return getDataRows(this.conceptList, this.sortSelection, this.displaySelection === 'Frequency' ? 'coverage_pair_group' : 'npmi');
        },
        menus() {
            return [
                {
                    name: 'Display',
                    selection: this.displaySelection,
                    options: [
                        [
                            {
                                type: 'menu',
                                options: ['Frequency', 'Correlation'],
                                showSelected: true,
                                selected: this.displaySelection,
                            },
                        ],
                    ],
                },
                {
                    name: 'Min Frequency',
                    selection: this.minConceptFreq,
                    options: [
                        [
                            {
                                type: 'inputNumber',
                                // Without having something in the options list,
                                // the dropdown shows "No Data"
                                options: ['inputNumber'],
                                settings: {
                                    min: 0,
                                    step: 10,
                                    max: 100,
                                },
                                showSelected: true,
                                selected: this.minConceptFreq,
                            },
                        ],
                    ],
                },
            ];
        },
    },
    watch: {
        config: {
            deep: true,
            handler() {
                this.setOptionsFromConfig();
            },
        },
        sortSelection(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.fetchData();
            }
        },
        sortAsc(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.fetchData();
            }
        },
        isZoomed(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.fetchData();
            }
        },
        displaySelection(display) {
            this.setHeadings(display);
        },
        minConceptFreq: debounce(function () {
            this.fetchData();
        }, 500),
        group(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.fetchData();
            }
        },
        queries(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.fetchData();
            }
        },
    },
    mounted() {
        this.setOptionsFromConfig();
        this.setHeadings(this.displaySelection);
        this.fetchData();
    },
    methods: {
        updateConfig() {
            const updated = Object.assign({}, this.config, {
                options: {
                    display: this.displaySelection,
                    minfreq: this.minConceptFreq,
                },
            });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            this.displaySelection = this.config?.options?.display ?? 'Correlation';
            this.minConceptFreq = this.config?.options?.minfreq ?? 5;
        },
        setDateRange(dates) {
            this.customDateRange = dates;
            this.updateConfig();
        },
        setHeadings(display) {
            const headings = [
                { label: 'Phrase', sortable: false },
                { label: 'Freq (#)', sortable: true, sortAsc: undefined, key: 'count_pair' },
            ];
            display === 'Correlation' && headings.push({ label: 'Correlation', sortable: true, sortAsc: false, key: 'npmi' });
            display === 'Frequency' &&
                headings.push({ label: 'Freq (%)', sortable: true, sortAsc: false, key: 'coverage_pair_group' });
            this.headings = headings;
            this.sortSelection = headings.slice(-1)[0].key || 'npmi';
            this.sortAsc = headings.slice(-1)[0].sortAsc || false;
        },
        onHeadingClick(headingIndex) {
            const heading = this.headings[headingIndex];
            const { sortAsc } = heading;
            // Reset and toggle sort mode
            this.headings.slice(1).forEach((h) => (h.sortAsc = undefined));
            this.sortAsc = heading.sortAsc = !sortAsc;
            if (heading.key) {
                this.sortSelection = heading.key;
            }
            this.$analytics.track.keyphrases.changeSort(this.sortSelection, this.sortAsc ? 'asc' : 'desc');
        },
        setTooltipStats(index) {
            this.tooltipStats = this.conceptList[index];
        },
        setChartDimensions(width, height) {
            this.width = width;
            this.height = height;
        },
        async fetchData(force = false) {
            this.hasErrored = false;
            try {
                // Analytics first, so that we track all attempts regardless of success.
                this.$analytics.track.keyphrases.load(this.conceptList.length);
                this.$emit('requires', 'key-phrases', {
                    // NOTE: filters get added by machinery in the parent Overview.vue.
                    queries: this.queries.map((q) => ({
                        name: q.name,
                        value: q.query_value,
                    })),
                    min_concept_freq: this.minConceptFreq,
                    sort_field: this.sortSelection,
                    sort_asc: this.sortAsc,
                    limit: this.limit,
                }, force, true, fetch_keyphrases_data);
            }
            catch (e) {
                this.hasErrored = true;
            }
        },
        setMenuSelection(name, [title, val]) {
            if (name === 'Display') {
                this.displaySelection = val;
                this.$analytics.track.keyphrases.changeDisplay(this.displaySelection);
            }
            if (name === 'Min Frequency') {
                this.minConceptFreq = val;
            }
            this.updateConfig();
        },
        getCsvData() {
            return generateCSV(this.conceptList, this.displaySelection);
        },
        getChartEl() {
            return this.$el.querySelector('div.content');
        },
        getSvgExportConfig() {
            const el = this.getChartEl();
            if (!el)
                return;
            const bb = el.getBoundingClientRect();
            // noinspection SpellCheckingInspection
            return { dims: { height: bb.height, width: bb.width }, css: `
        svg {
          top: 0;
          left: 0;
        }
        .chart {
          background-color: #fff;
          cursor: default;
        }
        text.first {
          text-anchor: start;
        }
        text.end{
          text-anchor: end;
        }
        rect.bar{
            fill: #11ACDF;
        }
        rect.background {
          fill: #F1F1F1;
        }
        rect.indicator {
          fill: #8064AA;
        }
        text.sorted {
          fill: #068CCC;
          font-weight: bold;
        }
        rect.legend-icon-one {
          fill: #11ACDF;
        }
        rect.legend-icon-two {
          fill: #8064AA;
        }
        .legend {
          cursor: default;
        }`,
            };
        },
        rowClicked(rowIndex) {
            if (!this.showDrilldown)
                return;
            const selectedPhrase = this.dataRows[rowIndex]?.label;
            this.$analytics.track.keyphrases.drillIntoPhrase(selectedPhrase);
        },
        // error panel items
        refresh() {
            window.location.reload();
        },
        reload() {
            this.fetchData();
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        },
        makePptSlide(pptx) {
            if (!this.dataRows.length)
                return;
            const slide = pptx.addSlide();
            makeBarChartSlide(pptx, slide, [{
                    name: 'Key Phrases',
                    labels: this.dataRows.map((r) => r.label),
                    values: this.dataRows.map((r) => r.columns[0].value),
                }], `${this.exportName} - Key Phrases`, 'Frequency', 'Phrase');
        }
    },
});
